"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const FORFAIT_LABELS: Record<string, string> = {
  "1-mois": "1 mois de soutien",
  "2-mois": "2 mois de soutien",
  "3-mois": "3 mois de soutien",
  "visite-ponctuelle": "une visite ponctuelle"
};
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 bg-white/50 border border-beige rounded-lg text-center animate-in fade-in zoom-in duration-500">
        <h3 className="font-serif text-3xl text-brun mb-4">Message envoyé</h3>
        <p className="text-brun/70 mb-8 max-w-md mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-caramel text-caramel hover:bg-caramel hover:text-white transition-all duration-300">
          Envoyer un autre message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
        {}
        <FormField control={control} name="name" rules={{
        required: "Ce champ est obligatoire"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brun/60">
                Nom Complet
              </FormLabel>
              <FormControl>
                <Input placeholder="Prénom Nom" {...field} className="h-12 bg-transparent border-0 border-b border-beige px-0 focus-visible:ring-0 focus-visible:border-caramel transition-colors placeholder:text-brun/30 text-brun" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Ce champ est obligatoire",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Email invalide"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brun/60">
                Adresse E-mail
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@exemple.com" {...field} className="h-12 bg-transparent border-0 border-b border-beige px-0 focus-visible:ring-0 focus-visible:border-caramel transition-colors placeholder:text-brun/30 text-brun" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brun/60">
                Numéro de téléphone
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+41 79 000 00 00" {...field} className="h-12 bg-transparent border-0 border-b border-beige px-0 focus-visible:ring-0 focus-visible:border-caramel transition-colors placeholder:text-brun/30 text-brun" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="interest" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brun/60">
                Je m'intéresse à
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="h-12 bg-transparent border-0 border-b border-beige px-0 focus-visible:ring-0 focus-visible:border-caramel transition-colors text-brun">
                    <SelectValue placeholder="Sélectionnez une option" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white border-beige rounded-lg">
                  <SelectItem value="postnatal">Accompagnement postnatal</SelectItem>
                  <SelectItem value="cooking">Ateliers de cuisine — Batch Cooking</SelectItem>
                  <SelectItem value="circle">Cercle Maman & Bébé</SelectItem>
                  <SelectItem value="other">Autre demande</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="message" rules={{
      required: "Ce champ est obligatoire"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brun/60">
              Votre Message
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Dis-moi en quelques mots comment je peux t’aider." {...field} className="min-h-[120px] bg-transparent border-0 border-b border-beige px-0 focus-visible:ring-0 focus-visible:border-caramel transition-colors placeholder:text-brun/30 text-brun resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="consent" rules={{
      required: "Vous devez accepter pour envoyer le message"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1 border-beige data-[state=checked]:bg-terracotta data-[state=checked]:border-terracotta" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-xs text-brun/70 font-normal cursor-pointer">
                J’accepte que Slow Mama utilise mes informations uniquement pour répondre à ma demande.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto px-12 h-14 rounded-lg bg-brun text-neutral-800 hover:bg-caramel/90 transition-all duration-500 text-sm font-semibold tracking-widest uppercase shadow-lg border border-brun/20">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Envoi en cours...
            </> : <>
              Envoyer mon message
              <ArrowRight className="size-4 ml-2" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const getInitialForfait = () => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const forfaitParam = params.get("forfait");
      if (forfaitParam === "carte-cadeau") {
        return {
          interest: "postnatal",
          message: "Bonjour Isabel, j'aimerais offrir une carte cadeau Slow Mama. Peux-tu m'aider à choisir une formule ?"
        };
      }
      if (forfaitParam === "nesting-party") {
        return {
          interest: "postnatal",
          message: "Bonjour Isabel, j'aimerais organiser ma nesting party. Peux-tu m'en dire plus ?"
        };
      }
      if (forfaitParam === "nesting-party-cadeau") {
        return {
          interest: "postnatal",
          message: "Bonjour Isabel, j'aimerais offrir une nesting party. Peux-tu m'aider à organiser ce cadeau ?"
        };
      }
      if (forfaitParam && FORFAIT_LABELS[forfaitParam]) {
        return {
          interest: "postnatal",
          message: `Bonjour, je suis intéressée par le forfait : ${FORFAIT_LABELS[forfaitParam]}. `
        };
      }
    }
    return {
      interest: "",
      message: ""
    };
  };
  const initialValues = getInitialForfait();
  return <section data-section-id="960" id="contact" className="relative bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 max-w-5xl">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div className="mb-16 md:mb-24">
            <span className="block text-[11px] font-bold uppercase tracking-[0.3em] text-caramel mb-6">Contacte-MOI</span>
            <h1 className="font-serif text-5xl md:text-7xl text-brun uppercase tracking-tight mb-6">
              Prenons contact
            </h1>
            <p className="font-serif italic text-xl md:text-2xl text-brun/60 max-w-2xl leading-relaxed">Une question, une envie d’en savoir plus ou besoin de réserver un appel découverte? Écris-moi, je te répondrai avec plaisir.</p>
          </div>

          {}
          <div className="relative">
            <FormProvider formId="contact-form-slowmama" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            interest: initialValues.interest,
            message: initialValues.message,
            consent: false
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            },
            consent: {
              required: true
            }
          }} submitText="Envoyer mon message" successMessage="Merci pour votre message. Je reviendrai vers vous très prochainement." errorMessage="Une erreur est survenue. Veuillez réessayer ultérieurement." className="bg-transparent">
              <ContactFormContent />
            </FormProvider>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 w-full h-px bg-beige/30" />
    </section>;
}